import { useCanvas } from '@components/ArtBoard/Canvas/hooks/events/useCanvas'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { AppState } from '@/types'
import { scale } from '../../algorithms'
import { ZOOM_MULTIPLIER } from '../../constants'
import { getCanvasState } from '../../helpers/canvas'
import { ELEMENT_GESTURE_MOVABLE_CONTAINER } from './useDragEvents'

type Event = {
  pointer: number[]
  diff?: number
}

type UseScaleEvents = {
  onWheelZoom: (deltaY: number, pointer: [number, number]) => void
  onZoomIn: (event?: Event) => void
  onZoomOut: (event?: Event) => void
  onResetZoom: () => void
  onFit: () => void
  onSetZoom: (scale: number) => void
}

const useScaleEvents = (): UseScaleEvents => {
  const dispatch = useDispatch()
  const dispatchCanvasUpdate = useCanvas()
  const { oldScale } = useSelector<
    AppState,
    {
      oldScale: number
      width: number
    }
  >(
    ({ canvas }) => ({
      oldScale: canvas.scale,
      width: canvas.width,
    }),
    shallowEqual
  )

  const onZoomIn = (event?: Event) => {
    sendGtmEvent(GtmTriggerName.ZOOM_IN)
    const ref = document.getElementById(ELEMENT_GESTURE_MOVABLE_CONTAINER)

    const { translate, ...state } = getCanvasState(ref)

    // This is to check if user is using touchpad or mouse wheel
    // Touchpads are more sensitive than mouse wheels
    // So we need to adjust the zoom accordingly
    const diff =
      event?.diff === undefined || event.diff > 2
        ? ZOOM_MULTIPLIER
        : (event.diff / 2) * ZOOM_MULTIPLIER

    const newScale = Math.min(state.scale + diff, 2)

    if (event) {
      const [tx, ty] = scale({
        pointer: event.pointer,
        newScale,
        oldScale: state.scale,
        ref,
        translate,
      })

      dispatchCanvasUpdate({
        scale: newScale,
        translate: [tx, ty],
      })
    } else {
      dispatchCanvasUpdate({
        scale: newScale,
        translate,
      })
    }

    if (oldScale !== newScale) {
      dispatch({
        type: CanvasActionTypes.SET_SCALE,
        payload: {
          scale: newScale,
        },
      })
    }
  }

  const onZoomOut = (event?: Event) => {
    sendGtmEvent(GtmTriggerName.ZOOM_OUT)
    const ref = document.getElementById(ELEMENT_GESTURE_MOVABLE_CONTAINER)

    const { translate, ...state } = getCanvasState(ref)

    // This is to check if user is using touchpad or mouse wheel
    // Touchpads are more sensitive than mouse wheels
    // So we need to adjust the zoom accordingly
    const diff =
      event?.diff === undefined || event.diff > 2
        ? ZOOM_MULTIPLIER
        : (event.diff / 2) * ZOOM_MULTIPLIER

    const newScale = Math.max(state.scale - diff, 0.1)

    if (event) {
      const [tx, ty] = scale({
        pointer: event.pointer,
        newScale,
        oldScale: state.scale,
        ref,
        translate,
      })

      dispatchCanvasUpdate({
        scale: newScale,
        translate: [tx, ty],
      })
    } else {
      dispatchCanvasUpdate({
        scale: newScale,
        translate,
      })
    }

    if (oldScale !== newScale) {
      dispatch({
        type: CanvasActionTypes.SET_SCALE,
        payload: {
          scale: newScale,
        },
      })
    }
  }

  const onWheelZoom = (deltaY: number, pointer: [number, number]) => {
    const ref = document.getElementById(ELEMENT_GESTURE_MOVABLE_CONTAINER)
    const state = getCanvasState(ref)
    // A 100px wheel movement changes zoom by about 10%, at any starting scale.
    const newScale = Math.max(
      0.1,
      Math.min(state.scale * Math.exp(-deltaY * 0.001), 2)
    )

    if (newScale === state.scale) {
      return
    }

    sendGtmEvent(deltaY < 0 ? GtmTriggerName.ZOOM_IN : GtmTriggerName.ZOOM_OUT)
    const translate = scale({
      pointer,
      newScale,
      oldScale: state.scale,
      ref,
      translate: state.translate,
    })

    dispatchCanvasUpdate({ scale: newScale, translate })
    dispatch({
      type: CanvasActionTypes.SET_SCALE,
      payload: { scale: newScale },
    })
  }

  const onResetZoom = () => {
    dispatchCanvasUpdate({
      scale: 1,
      translate: [0, 0],
    })

    dispatch({
      type: CanvasActionTypes.SET_SCALE,
      payload: {
        scale: 1,
      },
    })
  }

  const onFit = async () => {
    const ref = document.getElementById('printed-node')

    if (ref) {
      const currentWidth = ref.clientWidth

      const calculateWidth = Math.ceil((window.innerWidth / currentWidth) * 100)
      const { translate } = getCanvasState(ref.parentNode as HTMLElement)

      const scale =
        calculateWidth <= 100 ? Math.max(calculateWidth / 100, 0.1) : 1

      dispatch({
        type: CanvasActionTypes.SET_SCALE,
        payload: {
          scale,
        },
      })

      if (calculateWidth <= 100) {
        dispatchCanvasUpdate({
          scale,
        })

        setTimeout(() => {
          const root = ref.querySelector('.Tree-root') as HTMLElement
          const rootRect = root.getBoundingClientRect()

          const ty =
            ref.clientHeight < window.innerHeight
              ? -rootRect.top +
                translate[1] +
                window.innerHeight / 2 -
                ref.clientHeight / 2 +
                90
              : -rootRect.top + translate[1] + 90

          dispatchCanvasUpdate({
            translate: [0, ty],
          })
        }, 5)

        return
      }

      dispatchCanvasUpdate({
        scale,
        translate: [0, 0],
      })
    }
  }

  const onSetZoom = (scale: number) => {
    const ref = document.getElementById(ELEMENT_GESTURE_MOVABLE_CONTAINER)

    const { translate } = getCanvasState(ref)

    dispatchCanvasUpdate({
      scale,
      translate,
    })

    dispatch({
      type: CanvasActionTypes.SET_SCALE,
      payload: {
        scale,
      },
    })
  }

  return {
    onWheelZoom,
    onZoomIn,
    onZoomOut,
    onResetZoom,
    onFit,
    onSetZoom,
  }
}

export default useScaleEvents
